/* Copyright (c) 2025 Xperi. All Rights Reserved. */

import {
    ActionKernelType,
    ContentType,
    Entitlement,
    InternalRating,
    VodOttActionDetailsList,
    WatchActionKernel,
} from 'client-code/action-details';

import { LEFT_QUOTE, RIGHT_QUOTE } from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { ErrorTypes } from '../../../core/error';
import { VodPlayActionErrorMapper } from '../../../core/errormapper';
import { AxiosErrorAdapter } from '../../../core/errormapper/adapters';
import { NdvrInteractor } from '../../../core/interactors';
import { Locale, LocaleKeys } from '../../../core/locale';
import { Log } from '../../../core/logging';
import { ScreenTaskControllerTypes } from '../../../core/navigation/ScreenTaskControllerTypes';
import { PlaybackDataModelImpl } from '../../../core/playback/impls';
import { TOverlayOptions, TVodPlayActionStateType } from '../../../core/types';
import { DateTime } from '../../../core/utils';
import { hideOverlayDialog, showOverlayDialog } from '../../overlaydialog/overlaydialog.reducers';
import { IOverlayDialog } from '../../overlaydialog/overlaydialogprops';
import { setScreenTaskController } from '../../screens/screens.reducers';
import { notifyVodPlayActionState } from '../../vodplayaction/vodplayaction.reducers';
import { showPCPinOverlayWithProgramRating } from '../pcpinoverlay/pcpinoverlay.reducers';
import { prepareVodResumeStartOverOverlayForWatchAction } from '../vodresumestartoveroverlay/vodresumestartoveroverlay.reducers';

const GLOBAL_MOUSE_TRAP = 'HideStreamingOptionsOverlay';

export const StreamingOptionsOverlayType = {
    STREAMING_OPTIONS: 'streamingOptions',
    VOD_SOCU: 'vodSoCu',
} as const;
type StreamingOptionsOverlayType =
    (typeof StreamingOptionsOverlayType)[keyof typeof StreamingOptionsOverlayType];

// TODO : Below Method already defined in programscreen.reducers file and
// needs to be moved to a generic file
const doParentalControlCheck =
    (
        contentRatingInfo: {
            internalRatingList: InternalRating[];
            isAdult: boolean;
            contentType: string;
        },
        callback: Function,
        ...optionalParams: any[]
    ) =>
    (dispatch: Function) => {
        const { internalRatingList, isAdult, contentType } = contentRatingInfo;

        const services = new NdvrInteractor();
        const doesPcViolated = services.checkPCViolationsWithRatings(
            internalRatingList,
            isAdult,
            contentType,
        );
        const ratingName = services.getRestrictedRatingName(internalRatingList);
        if (doesPcViolated) {
            dispatch(
                showPCPinOverlayWithProgramRating(
                    () => {
                        dispatch(callback(...optionalParams));
                    },
                    isAdult,
                    ratingName,
                ),
            );
        } else {
            dispatch(callback(...optionalParams));
        }
    };

const getFormattedTextForLargeButtons = (action: any) => {
    const { entitlement } = action.kernel as any;
    let formattedEntitlement;
    if (entitlement === Entitlement.Subscribed) {
        formattedEntitlement = Locale.getString(
            LocaleKeys.STREAMING_OPTIONS_OVERLAY_ENTITLEMENT_SUBSCRIBED,
        );
    }
    if (entitlement === Entitlement.Subscribable) {
        formattedEntitlement = Locale.getString(
            LocaleKeys.STREAMING_OPTIONS_OVERLAY_ENTITLEMENT_SUBSCRIBABLE,
        );
    }
    const foramttedTime = new DateTime((action as any).startTime).strftime('%a %M/%D %l:%i%p');

    return `${(action as any).videoResolution.toUpperCase()}#${(action.kernel as any).channel?.callSign || ''}#${foramttedTime}#${formattedEntitlement}`;
};

const showStreamingOptionsOverlay = (type: StreamingOptionsOverlayType) => (dispatch: Function) => {
    const { actionDetails } = PlaybackDataModelImpl.getInstance().getActionDetailsList();
    let overlayTitle = '';
    if (type === StreamingOptionsOverlayType.STREAMING_OPTIONS) {
        overlayTitle = Locale.getString(LocaleKeys.STREAMING_OPTIONS_OVERLAY_TITLE);
    } else if (type === StreamingOptionsOverlayType.VOD_SOCU) {
        overlayTitle = actionDetails.watchActionsList?.[0].providerDisplayName || '';
    }
    const overlayOptions: TOverlayOptions[] =
        (actionDetails as VodOttActionDetailsList)?.watchActionsList?.map(
            (action): TOverlayOptions => {
                return {
                    text: getFormattedTextForLargeButtons(action),
                    value: { action },
                };
            },
        ) || [];
    let subtitle: string = actionDetails.title || '';

    if (actionDetails.contentType === ContentType.Episode) {
        const { episodeNumber, seasonNumber, episodeTitle } = actionDetails;
        const seasonEpisodeText: string = AppUtils.generateSeasonEpisodeText(
            seasonNumber || 0,
            episodeNumber || 0,
        );
        subtitle += ` - ${seasonEpisodeText} ${LEFT_QUOTE}${
            episodeTitle || Locale.getString(LocaleKeys.TITLE_NOT_AVAILABLE)
        }${RIGHT_QUOTE}`;
    } else if (actionDetails.contentType === ContentType.Movie) {
        const { movieYear } = actionDetails;
        const movieYearText: string = movieYear ? ` (${movieYear})` : '';
        subtitle = `${subtitle}${movieYearText}`;
    }
    const overlayProps: IOverlayDialog = {
        hasDimmer: true,
        hasSeperator: false,
        mouseTrapValue: GLOBAL_MOUSE_TRAP,
        options: overlayOptions,
        subtitle,
        title: overlayTitle,
        isLargeSizeOverlay: true,
    };
    dispatch(hideOverlayDialog());
    dispatch(setScreenTaskController(ScreenTaskControllerTypes.STREAMING_OPTIONS_OVERLAY));
    dispatch(showOverlayDialog(overlayProps));
};

export const prepareStreamingOptionsOverlay =
    (type: StreamingOptionsOverlayType) => (dispatch: Function) => {
        try {
            const { actionDetails } = PlaybackDataModelImpl.getInstance().getActionDetailsList();
            const { watchActionsList } = actionDetails;
            if (watchActionsList.length === 0) {
                const vodPlayError = new VodPlayActionErrorMapper(
                    new AxiosErrorAdapter(),
                ).getErrorByCode(ErrorTypes.VOD_PLAY_ACTION_PROGRAM_UNAVAILABLE_V433);
                dispatch(
                    notifyVodPlayActionState({
                        type: TVodPlayActionStateType.ERROR,
                        error: vodPlayError,
                    }),
                );
            } else if (type === StreamingOptionsOverlayType.STREAMING_OPTIONS) {
                // always show streaming options overlay when type is streaming options
                dispatch(showStreamingOptionsOverlay(type));
            } else {
                const action = watchActionsList[0];
                const isStartOver =
                    'isStartOver' in action.kernel ? (action.kernel as any).isStartOver : false;

                PlaybackDataModelImpl.getInstance().setActionDetails(action);
                if (
                    watchActionsList.length === 1 &&
                    (action.kernel as WatchActionKernel).actionKernelType.toLocaleLowerCase() ===
                        ActionKernelType.WatchFromSoCuActionKernel.toLocaleLowerCase()
                ) {
                    dispatch(prepareVodResumeStartOverOverlayForWatchAction(action, !!isStartOver));
                } else {
                    dispatch(showStreamingOptionsOverlay(type));
                }
            }
        } catch (error) {
            Log.e('prepareStreamingOptionsOverlay', 'error', error);
            // decide what to do when error occurs
        }
    };

export const onKeyOkStreamingOptionsOverlay =
    (actionDetail: any) => (dispatch: Function, getState: Function) => {
        const { overlayHighlight, overlayDialog } = getState();
        const { verticalPosition } = overlayHighlight;
        const { uiProperties } = overlayDialog;
        const { options } = uiProperties;
        const selectedOption: TOverlayOptions = options[verticalPosition];
        const { action } = selectedOption.value;
        const isStartOver =
            'isStartOver' in action.kernel ? (action.kernel as any).isStartOver : false;
        const { internalRatingList = [], isAdult = false } = action.kernel;
        const { contentType } = actionDetail;

        dispatch(hideOverlayDialog());
        if (
            action.kernel.actionKernelType.toLowerCase() ===
            ActionKernelType.WatchFromSoCuActionKernel.toLowerCase()
        ) {
            PlaybackDataModelImpl.getInstance().setActionDetails(action);
            // Launch VOD resume start overlay if parental control check passes.
            dispatch(
                doParentalControlCheck(
                    { internalRatingList, isAdult, contentType },
                    prepareVodResumeStartOverOverlayForWatchAction,
                    action,
                    !!isStartOver,
                ),
            );
        } else {
            /**
             * according to requirement :
             * http://w3-engr/MyWorld/UserInterface/Hydra/Specs/cross_platform/whispers_overlays.html#all_streaming_options_overlay
             * J : Press OK for any offer in the list
             * : run Process On Demand Request for the selected item.
             */
            dispatch(
                notifyVodPlayActionState({
                    type: TVodPlayActionStateType.INIT,
                    vodRequiredParams: {
                        itemType: 'offer',
                        id: action.kernel.offerId,
                        partnerId: action.kernel.partnerId || '',
                        contentSupplierPartnerId: action.kernel.contentSupplierPartnerId || '',
                    },
                }),
            );
        }
    };

export const onMouseClickStreamingOptionsOverlay =
    (actionDetail) => (dispatch: Function, getState: Function) => {
        const { value } = getState().inputFactory;

        if (value === GLOBAL_MOUSE_TRAP) {
            dispatch(hideOverlayDialog());
            return;
        }

        dispatch(onKeyOkStreamingOptionsOverlay(actionDetail));
    };
